import { dateFormats, tickFormats } from '../common/constants/chart-options';
import { max, min, range, ticks } from 'd3-array';
import { scaleLinear, scaleTime } from 'd3-scale';
import { stack, stackOffsetSilhouette } from 'd3-shape';
import { timeFormat, timeParse } from 'd3-time-format';

import { every } from 'lodash';
import moment from 'moment';
import { select } from 'd3-selection';

/* Generates default array of dates to correspond with tick values  */
export const defaultDateTickValues = ( interval, data ) => {
    return data
        .filter( ( item, index ) => {
            if( interval === 'day' ){
                // every two days
                return index % 2 === 0;
            }
            return item;
        } )
        .map( item => item.date );
};

/* Returns boolean indicating whether all data values for a given array of keys are 0 */
export const dataIsZero = ( data, keys ) => {
    return every( keys, key => {
        const isZero = every( data, ( row ) => {
            return parseInt( row[key], 10 ) === 0;
        } );
        return isZero;
    } );
};

/* Calculates inner dimensions use to define a scale's range */
export const getInnerDimension = ( dimension, firstMargin = 0, secondMargin = 0 ) => {
    return dimension - ( firstMargin + secondMargin );
};

/* Returns array of ticks values to display in Axis & Grid */
export const getTickValues = ( type, start, end, modifier ) => {
    switch( type ){
        case 'ticks':
            // modifier = number of ticks b/w start & stop
            // inclusive if start & end are consistent with inferred step
            return [...ticks( start, end, modifier )];
        case 'range':
        default:
            // modifier = step to increment b/w start & end
            // end value excluded from result
            return [...range( start, end, modifier )];
    }
};

/* Returns formatted number with precision depending on prescence of remainder */
export const getNumberFormat = ( number, suffix = '' ) => {
    let format;
    const remainder = number % 1;

    if( remainder === 0 ){
        format = tickFormats.NO_PRECISION;
    }
    else{
        format = tickFormats.STANDARD_PRECISION;
    }
    return format( number ) + suffix;
};

/* Returns formatted date depending on the selected interval */
export const getTimeFormat = ( allDates, date, dateIndex, interval ) => {
    switch( interval ){
        case 'month':
            return timeFormat( dateFormats.MONTH )( date );
        case 'quarter':
            return `Q${moment( date ).utc().quarter()}`;
        case 'day':
        case 'week':
        default:
            if( dateIndex === 0 ){
                return timeFormat( dateFormats.DAY_AND_MONTH )( date );
            }

            const previousDate = allDates[ dateIndex - 1];
            if( previousDate ){
                const previousMonth = moment( previousDate ).month();
                const currentMonth = moment( date ).month();

                // only show dayAndMonth on the first occurrence of the month in the tickValues
                if( previousMonth === currentMonth ){
                    return timeFormat( dateFormats.DAY )( date );
                }
                else{
                    return timeFormat( dateFormats.DAY_AND_MONTH )( date );
                }
            }
    }
};

/* Helpers for calculating stack domain */
export const getStackMin = layer => min( layer, function( d ){
    return +d[0];
} );
export const getStackMax = layer => max( layer, function( d ){
    return +d[1];
} );

/* Constructs D3 stack generator
 *   - stackOffsetWiggle: minimizes weighted wiggle of layers
 *   - stackOrderInsideOut: larger series on inside, smaller on outside
 *   - keys: determines series in stack
 */
export const generateStack = ( keys ) => stack()
    .offset( stackOffsetSilhouette )
    .keys( keys );

/* D3 scale: map an input domain to an output range
 *  - domain: interpolated from data
 *  - range: based on dimensions of svg
 */
export const generateScale = ( scaleType, domainVals, rangeVals ) => {
    const scale = {
        linear: scaleLinear,
        time: scaleTime,
    };

    return scale[scaleType]()
        .domain( domainVals )
        .range( rangeVals )
        .clamp( true );
};

/* Parses date strings in a given format into D3-readable Date objects */
export const parseTime = ( format ) => timeParse( format );

/* Pads input date using moment date operation ('add', 'subtract')
 * depending on the input range [max, min] of a set of dates
 */
export const padDate = ( operation, inputDate, range ) => {
    const amountPad = {
        add: {
            '30': 1,
            '61': 3,
            '91': 5,
            max: 15
        },
        subtract: {
            '30': 2,
            '61': 4,
            '91': 7,
            max: 30
        }
    };

    const difference = moment( range[1] ).diff( moment( range[0] ), 'days' );
    let paddedInput = inputDate;

    if( difference <= 30 ){
        paddedInput = moment['fn'][operation].call( moment( inputDate ), amountPad[operation]['30'], 'days' );
    }
    else if( difference <= 61 ){
        paddedInput = moment['fn'][operation].call( moment( inputDate ), amountPad[operation]['61'], 'days' );
    }
    else if( difference <= 91 ){
        paddedInput = moment['fn'][operation].call( moment( inputDate ), amountPad[operation]['91'], 'days' );
    }
    else{
        paddedInput = moment['fn'][operation].call( moment( inputDate ), amountPad[operation]['max'], 'days' );
    }

    return paddedInput.toDate();
};

/* Returns value rounded up/down to nearest multiple of multiplier */
export const roundToMultiplier = ( input, multiplier, direction ) => {
    let rounded;

    switch( direction ){
        case 'down':
            rounded = multiplier * Math.floor( input / multiplier );
            break;
        case 'up':
        default:
            rounded = multiplier * Math.ceil( input / multiplier );
            break;
    }

    return rounded;
};

/* Callback that makes svg <text> wrap according to specified width
 *   - see https://bl.ocks.org/mbostock/7555321
 */
export const wrapText = ( text, textWidth ) => {
    text.each( function(){
        const label = select( this );
        const lineHeight = 1.1;
        const words = label.text().split( /\s+/ ).reverse();
        const x = label.attr( 'x' );
        const y = label.attr( 'y' );
        const dy = parseFloat( label.attr( 'dy' ) );

        let tspan = label
            .text( null )
            .append( 'tspan' )
            .attr( 'x', x )
            .attr( 'y', y )
            .attr( 'dy', dy + 'em' );

        let line = [],
            lineNumber = 0,
            word = [];

        while( word ){
            line.push( word );
            tspan.text( line.join( ' ' ) );

            if( tspan.node().getComputedTextLength() > textWidth ){
                line.pop();
                tspan.text( line.join( ' ' ) );
                line = [word];
                tspan = label
                    .append( 'tspan' )
                    .attr( 'x', x )
                    .attr( 'y', y )
                    .attr( 'dy', ++lineNumber * lineHeight + dy + 'em' )
                    .text( word );
            }

            word = words.pop();
        }
    } );
};

export const getXOffsetForMaxVolumeText = ( maxCount ) => {
    return 46 + maxCount.toString().length * 2;
};
